import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import { useSelector } from 'react-redux';

const SocketContext = createContext();

// A branch id is a uuid. 'all' is a view mode, not a branch.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isBranchId = (value) => UUID_RE.test(String(value || ''));

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const { user, isAuthenticated } = useSelector((state) => state.auth);
    const garageId = useSelector((state) => state.auth.activeGarageId);

    useEffect(() => {
        let newSocket;

        // `isAuthenticated` alone. This used to also require `garageId`, so a
        // company-level user with no home branch — activeGarageId null — never
        // opened a socket at all: no live updates and no notification toasts
        // either. Room membership is resolved server-side now, so a branch id
        // is no longer a precondition for connecting.
        if (isAuthenticated) {
            // Connect to the socket server using relative path (proxied)
            // Connect to the socket server using relative path (proxied)
            const socketUrl = '/';

            newSocket = io(socketUrl, {
                withCredentials: true,
                transports: ['websocket'],
                // Backend enforces this in io.use (server.js) — a connection without a
                // valid token is rejected before any 'connection'/room-join handler runs.
                auth: { token: localStorage.getItem('token') || undefined },
                reconnection: true,
                reconnectionDelay: 1000,
                // Keep retrying (backoff caps at 5 s). With 5 attempts the client
                // gave up after ~17 s, so a backend restart (event-loop guard,
                // deploy: 35-45 s) left live updates dead until a page reload.
                reconnectionAttempts: Infinity,
            });

            newSocket.on('connect', () => {
                console.log('Socket connected');

                // Only a real branch id is a room. activeGarageId also carries the
                // sentinel 'all' when the branch switcher is on "all branches"
                // (Dashboard.js compares it to that literal), and emitting that
                // asked the server to look up a garage whose id is the string
                // 'all' — a uuid column, so Postgres raised 22P02 inside a socket
                // handler and took the backend down with it.
                // No argument, resolved server-side from the authenticated
                // identity — the ONLY join that works on "all branches".
                //
                // Everything below picks a room name from `activeGarageId`,
                // which is the branch switcher's value: it is the literal 'all'
                // when the user is viewing every branch, and null for a
                // company-level user with no home branch. Neither is a uuid, so
                // the guard below correctly refuses to send it — and the net
                // effect was that an admin on "all branches", which is how this
                // app is normally used, joined nothing and got no live updates
                // whatsoever. Sending no id at all removes the question.
                newSocket.emit('join-my-garages');

                if (isBranchId(garageId)) {
                    // BOTH rooms, and the first one is the fix.
                    //
                    // The backend emits its ~25 live work events — job-card-updated,
                    // task-status-updated, sub-task-updated, job-comment-added,
                    // part-approved and the rest — to `garage-<id>`. Only the
                    // `notification` toasts go to `garage-<id>-admins`. This client
                    // joined the -admins room and nothing else, so every work event
                    // has been emitted into a room with no members since the socket
                    // layer shipped. The listeners that depend on them — the job
                    // card's own sub-task stream, the mechanic's task list, the
                    // comment thread — have never once fired, and the 10s poll has
                    // been doing all of the work while looking like a safety net.
                    //
                    // Server-side both joins are permission-checked identically
                    // (canAccessGarage) and silently ignored otherwise — so this
                    // grants no AUTHORITY the -admins room did not.
                    //
                    // It does grant reach, and that distinction cost a blocker:
                    // the work room's payloads had gone unexamined for as long as
                    // nobody was listening, and several shipped whole records —
                    // task lines with prices and cost prices, a comment with its
                    // author's email. Joining is what makes those live. They are
                    // slimmed to identifiers in the same change (see
                    // backend/tests/controllers/workRoomPayloadShape.test.js,
                    // which fails if a fat payload is ever added back), and the
                    // rule for anything new on this room is: broadcast that
                    // something changed, never what it changed to.
                    newSocket.emit('join-garage', garageId);
                    newSocket.emit('join-garage-admin', garageId);
                }
            });

            newSocket.on('connect_error', (err) => {
                // Silently handle connection errors - socket server may not be running
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Socket connection unavailable:', err.message);
                }
            });

            newSocket.on('disconnect', () => {
                if (process.env.NODE_ENV === 'development') {
                    console.log('Socket disconnected');
                }
            });

            setSocket(newSocket);
        }

        return () => {
            if (newSocket) {
                newSocket.disconnect();
            }
        };
    }, [isAuthenticated, garageId]);

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};
